import { useAuth } from '@/providers/AuthProvider';
import { useTranslation } from 'react-i18next';
import { Card,  CardDescription, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, 
  CheckCircle2, 
  ArrowRight,
  Building2,
  ClipboardCheck,
  Users,
  Settings,
  Home,
  FileText,
  Upload,
  Eye,
  ListChecks
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function GuidelinesPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  if (!user) return null;
  
  const role = user.roles[0] as 'super_admin' | 'admin' | 'manager' | 'auditor';

  // Step component for consistency
  const Step = ({ 
    number, 
    title, 
    description, 
    icon: Icon,
    substeps 
  }: { 
    number: number; 
    title: string; 
    description: string; 
    icon: any;
    substeps?: { text: string; detail?: string }[];
  }) => {
    // Helper function to convert URLs in text to clickable links
    const renderTextWithLinks = (text: string) => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = text.split(urlRegex);
      
      return parts.map((part, index) => {
        if (part.match(urlRegex)) {
          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return <span key={index}>{part}</span>;
      });
    };

    return (
      <div className={cn(
        "flex gap-4 p-4 rounded-lg bg-muted/50",
        isRTL && "flex-row-reverse"
      )}>
        <div className="flex-shrink-0">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold">
            {number}
          </div>
        </div>
        <div className={cn("flex-1", isRTL && "text-right")}>
          <div className={cn("flex items-center gap-2 mb-2", isRTL && "flex-row-reverse justify-end")}>
            <Icon className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-lg">{title}</h3>
          </div>
          <p className="text-muted-foreground mb-3">{description}</p>
          {substeps && substeps.length > 0 && (
            <div className="space-y-2 mt-3">
              {substeps.map((substep, idx) => (
                <div key={idx} className={cn("flex gap-2 items-start", isRTL && "flex-row-reverse")}>
                  <ArrowRight className={cn("w-4 h-4 mt-0.5 text-primary flex-shrink-0", isRTL && "rotate-180")} />
                  <div>
                    <p className="text-sm font-medium">{substep.text}</p>
                    {substep.detail && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {renderTextWithLinks(substep.detail)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Role-based guidelines
  const guidelines = {
    super_admin: {
      title: "Super Admin Guidelines",
      description: "Complete system control with full access to all features",
      color: "bg-red-500",
      steps: [
        {
          number: 1,
          title: "Managing Property Templates",
          description: "Set up property types and their assessment standards before creating properties",
          icon: Settings,
          substeps: [
            { 
              text: "Navigate to Templates:", 
              detail: "Click 'Templates' in the left sidebar or visit https://qpsaudit.com/admin/templates to see all property type templates in the system." 
            },
            { 
              text: "View Existing Templates:", 
              detail: "You'll see a list of all property types (e.g., Hotel, Resort, Villa). Each card shows the template name, total number of standards, and action buttons." 
            },
            { 
              text: "Click on a Template:", 
              detail: "Select any template card to open its details page (e.g., https://qpsaudit.com/admin/templates/1). This page displays all departments, touchpoints, and standards organized hierarchically." 
            },
            { 
              text: "Add Standards - Import from Excel:", 
              detail: "On the template details page at https://qpsaudit.com/admin/templates/[id], click the 'Import Excel' button (top right of standards table) → A file picker opens → Select your prepared Excel or CSV file (.xlsx, .xls, or .csv formats supported, max 10MB) → System uploads and processes the file → Imports all standards with their departments, touchpoints, sub-touchpoints, titles, and bilingual text → Shows success message with count (e.g., 'Successfully imported 150 standards') → Page auto-refreshes to display all new standards → Best for bulk imports of 50+ standards at once." 
            },
            { 
              text: "Add Standards - Manually (Individual):", 
              detail: "Click 'Add Standard' button at https://qpsaudit.com/admin/templates/[id]/add-standards → A form appears with dropdowns and text areas: 1) Department dropdown (select from existing: 'Housekeeping', 'Front Desk', 'Restaurant', etc.) 2) Touchpoint dropdown (select touchpoint related to department) 3) Sub-Touchpoint dropdown (optional - select if applicable or leave as 'None') 4) Title dropdown (select the area/category) 5) Standard Text (English) - type the quality standard in English 6) Standard Text (Arabic) - type the same standard in Arabic → Click 'Add to List' button (standard is added to temporary local list, NOT saved yet) → Repeat steps 1-6 to add more standards quickly (form retains dropdowns but clears text areas) → When finished adding all standards, scroll down and click 'Save All Standards' button → All standards in the list are batch-saved to the server at once → Success toast shows 'X standards saved successfully' → Automatically redirected back to template details page." 
            },
            { 
              text: "Filter & Search Standards:", 
              detail: "Use the search bar at the top to search by standard name (e.g., type 'bed' to find all bed-related standards) → Use the Department dropdown filter to show only standards from one department (e.g., select 'Housekeeping') → Use the Touchpoint dropdown to narrow further (e.g., select 'Room Cleaning') → Combine all filters together for precise results (e.g., search 'clean' + Department 'Housekeeping' + Touchpoint 'Bathroom') → Click 'Clear Filters' to reset." 
            },
            { 
              text: "Edit Existing Standards:", 
              detail: "Click the 'Edit' icon (pencil) next to any standard → Modify Department, Touchpoint, Name, or Description → Click 'Save Changes' → Updates apply immediately to all properties using this template." 
            },
            { 
              text: "Delete Standards:", 
              detail: "Click the 'Delete' icon (trash) next to any standard → Confirm deletion in the popup dialog → Standard is permanently removed. Warning: This affects all properties using this template." 
            },
            { 
              text: "Export Template:", 
              detail: "Click 'Export to Excel' button at the top → System generates an Excel file with all standards, departments, and touchpoints → Download for documentation, backup, or sharing with stakeholders." 
            }
          ]
        },
        {
          number: 2,
          title: "Creating Properties",
          description: "Register new properties in the system with complete details",
          icon: Building2,
          substeps: [
            { 
              text: "Navigate to Properties:", 
              detail: "Click 'Properties' in the sidebar or visit https://qpsaudit.com/properties to see all registered properties in the system." 
            },
            { 
              text: "Start Creating New Property:", 
              detail: "Click the 'Add Property' button (top right corner) → This opens a 3-step wizard at https://qpsaudit.com/properties/create/step-1" 
            },
            { 
              text: "Step 1 - Basic Information:", 
              detail: "Fill in: Property Name (e.g., 'Grand Hyatt Dubai'), Location/Address (full address including city and country), Property Type (Hotel/Resort/Villa/Apartment), Manager Name (person responsible), Manager Phone (with country code), Manager Email (for account access), Additional Notes (optional details). Click 'Next' to proceed to Step 2." 
            },
            { 
              text: "Step 2 - Select Template:", 
              detail: "Choose which Property Type Template to apply (e.g., '5-Star Hotel Standards', 'Boutique Resort Standards') → This determines which quality standards will be used for assessments → Preview shows total departments, touchpoints, and standards included → Click 'Next' to proceed to Step 3." 
            },
            { 
              text: "Step 3 - Review & Confirm:", 
              detail: "Review all entered information carefully → Check property details are correct → Verify the selected template is appropriate → Click 'Create Property' to finalize → Property is immediately created and appears in the properties list at https://qpsaudit.com/properties" 
            },
            { 
              text: "After Creation:", 
              detail: "Property receives a unique ID and dedicated dashboard URL (e.g., https://qpsaudit.com/properties/12) → You can now create assessments for this property → Assign a Manager user to this property for dashboard access → Property is ready for quality audits." 
            }
          ]
        },
        {
          number: 3,
          title: "Managing Users",
          description: "Create and assign users with specific roles",
          icon: Users,
          substeps: [
            { 
              text: "Navigate to Users:", 
              detail: "Click 'Users' in the sidebar or visit https://qpsaudit.com/users to see all system users. You'll see a table with user names, emails, roles, and assigned properties." 
            },
            { 
              text: "Start Creating New User:", 
              detail: "Click 'Create New User' button (top right) → Opens creation form at https://qpsaudit.com/users/create-user" 
            },
            { 
              text: "Fill Basic User Information:", 
              detail: "Enter: Full Name (e.g., 'Ahmed Hassan'), Email Address (used for login, must be unique), Password (minimum 8 characters, user will use this to login), Phone Number (with country code, e.g., +971501234567)" 
            },
            { 
              text: "Select User Role - Admin:", 
              detail: "Choose 'Admin' role if the user needs to: View all properties and templates (read-only), Manage all users (create/edit/delete), Create and manage assessments for any property, View all assessment results and reports, Export reports system-wide. Admins have broad access but cannot edit templates or properties." 
            },
            { 
              text: "Select User Role - Manager:", 
              detail: "Choose 'Manager' role if the user needs to: View ONE specific property's dashboard only, Monitor assessment results for their property, Review missed standards and performance trends, View photos and audio evidence, Track quality improvements over time. Managers have read-only access limited to their assigned property. After selecting Manager role, you MUST assign their property from the dropdown list." 
            },
            { 
              text: "Select User Role - Auditor:", 
              detail: "Choose 'Auditor' role if the user needs to: Perform quality assessments on-site, Assess standards (Met/Not Met/N/A), Upload photos and audio evidence, Submit completed assessments, View only their assigned assessments. Auditors are assigned to specific assessments when you create them (not during user creation)." 
            },
            { 
              text: "Save User:", 
              detail: "Click 'Create User' button → User is created immediately → They can now login at https://qpsaudit.com/login using their email and password → User sees role-appropriate interface based on their assigned role." 
            },
            { 
              text: "Edit Existing Users:", 
              detail: "Return to https://qpsaudit.com/users → Click 'Edit' icon next to any user → Modify name, email, phone, role, or assigned property → Cannot change password here (users must reset via forgot password) → Click 'Save Changes' to update." 
            },
            { 
              text: "Delete Users:", 
              detail: "Click 'Delete' icon next to any user → Confirm deletion in the popup → User account is permanently removed → They can no longer login to the system. Warning: Delete only inactive users." 
            }
          ]
        },
        {
          number: 4,
          title: "Creating Assessments",
          description: "Schedule quality assessments for properties",
          icon: ClipboardCheck,
          substeps: [
            { 
              text: "Navigate to Assessments:", 
              detail: "Click 'Assessments' in the sidebar or visit https://qpsaudit.com/assessments to see all assessments. The list shows: property name, assigned auditor, date, status (Draft/In Progress/Completed), and action buttons." 
            },
            { 
              text: "Start Creating Assessment:", 
              detail: "Click 'Create Assessment' button (top right) → Opens creation form at https://qpsaudit.com/assessments/create-assessment" 
            },
            { 
              text: "Select Property:", 
              detail: "Choose which property to assess from the dropdown → All registered properties appear in the list → Selected property determines which template standards will be used → Property name and type display after selection." 
            },
            { 
              text: "Choose Assessment Date:", 
              detail: "Click the date picker → Select when the assessment will be performed → Can be today or any future date → Date helps organize and schedule auditor workload → Past dates are disabled to prevent backdating." 
            },
            { 
              text: "Define Assessment Scope - Departments:", 
              detail: "You see checkboxes for all departments in the property's template (e.g., Housekeeping, Front Desk, Restaurant, Pool & Beach) → Check the departments you want to assess → Leave unchecked departments you want to skip → Selecting a department includes all its touchpoints by default → Use this to focus on specific operational areas." 
            },
            { 
              text: "Define Assessment Scope - Touchpoints:", 
              detail: "After selecting departments, expand each one to see its touchpoints → Uncheck specific touchpoints you want to exclude (e.g., assess 'Housekeeping' but skip 'Laundry' touchpoint) → This gives granular control over what gets assessed → Auditor will only see selected departments and touchpoints when performing the assessment." 
            },
            { 
              text: "Assign Auditor:", 
              detail: "Select from dropdown which Auditor user will perform this assessment → Only users with 'Auditor' role appear in the list → Assigned auditor will see this assessment when they login → They receive notification of the new assignment." 
            },
            { 
              text: "Save Assessment:", 
              detail: "Click 'Create Assessment' button → Assessment is created with status 'Draft' → Appears immediately in the assessments list → Assigned auditor can now access it at https://qpsaudit.com/assessments and click 'Perform' to start working → You can edit the assessment details anytime before auditor starts." 
            }
          ]
        },
        {
          number: 5,
          title: "Monitoring Assessments",
          description: "Track assessment progress and review results",
          icon: Eye,
          substeps: [
            { 
              text: "View All Assessments:", 
              detail: "Visit https://qpsaudit.com/assessments to see every assessment across all properties → Table shows: property name, assigned auditor name, assessment date, current status, and action buttons → Use search bar to find specific properties or auditors." 
            },
            { 
              text: "Understanding Assessment Status:", 
              detail: "Draft (gray badge) = Created but auditor hasn't started yet, no standards assessed → In Progress (blue badge) = Auditor has started working, some standards assessed but not submitted → Completed (green badge) = Auditor submitted the assessment, all results finalized and visible." 
            },
            { 
              text: "View Assessment Details:", 
              detail: "Click 'Perform' button next to any assessment → Opens assessment interface at https://qpsaudit.com/assessments/perform/[id] → Left sidebar shows departments and touchpoints → Click department → Click touchpoint → View all standards with their assessment status (Met/Not Met/N/A) → Green = Met, Red = Not Met, Gray = N/A." 
            },
            { 
              text: "Review Photos Uploaded by Auditor:", 
              detail: "While viewing assessment details, each standard may have camera icons → Click the camera icon or photo thumbnail to view full-size images → Photos show evidence of issues (for Not Met) or excellent examples (for Met) → Multiple photos may exist per standard → Use arrow keys or swipe to navigate between photos → Click 'X' to close photo viewer." 
            },
            { 
              text: "Listen to Audio Notes:", 
              detail: "Standards with audio have microphone icons and audio player → Click play button to listen to auditor's verbal explanation → Audio provides context: why standard wasn't met, severity of issue, suggestions for improvement → Pause, rewind, or adjust volume as needed → Useful for understanding complex findings." 
            },
            { 
              text: "Export Assessment Report:", 
              detail: "On the assessment details page, click 'Export PDF' button (top right) → System generates comprehensive PDF report with: property details, assessment date and auditor, all departments and touchpoints assessed, standards with Met/Not Met/N/A results, embedded photos, summary statistics (total standards, compliance rate, missed standards count) → PDF downloads automatically → Use for documentation, management reports, or client presentations." 
            },
            { 
              text: "View Property Dashboard:", 
              detail: "Click on property name to visit its dashboard (e.g., https://qpsaudit.com/properties/12) → Dashboard shows: all assessments for this property, compliance trends over time (line chart), missed standards section with recurring issues, department performance breakdown, touchpoint performance analysis → Use to identify patterns and prioritize improvements." 
            }
          ]
        },
        {
          number: 6,
          title: "Using the Dashboard",
          description: "Access analytics and system overview",
          icon: FileText,
          substeps: [
            { 
              text: "Access System Dashboard:", 
              detail: "Click 'Dashboard' in the sidebar or visit https://qpsaudit.com/dashboard after login → This is your main overview screen showing system-wide statistics and recent activity." 
            },
            { 
              text: "View Key Metrics Cards:", 
              detail: "Top row shows summary cards: Total Properties (count of all registered properties), Total Assessments (all assessments created), Total Users (all users in system), Completion Rate (percentage of completed vs total assessments) → Each card shows the number and a trend indicator (up/down from previous period)." 
            },
            { 
              text: "Monitor Assessment Status:", 
              detail: "Assessment status chart shows breakdown: How many are Draft (not started), In Progress (being performed), Completed (submitted) → Visual pie chart or bar graph → Click on any segment to filter and see those specific assessments." 
            },
            { 
              text: "Track Performance Trends:", 
              detail: "Line chart shows compliance rates over time (last 30/60/90 days) → Y-axis = compliance percentage (0-100%), X-axis = dates → See if quality is improving or declining system-wide → Hover over data points to see exact values." 
            },
            { 
              text: "Review Recent Activity:", 
              detail: "Activity feed shows recent actions: New assessments created, Assessments completed by auditors, New properties added, New users registered → Each entry shows timestamp and actor (who did it) → Click on any activity to view details." 
            },
            { 
              text: "Identify Properties Needing Attention:", 
              detail: "'Properties At Risk' section highlights properties with: Low compliance rates (below 70%), Increasing missed standards over time, Assessments overdue or pending → Sorted by priority (worst first) → Click property name to view its detailed dashboard and take action." 
            }
          ]
        },
        {
          number: 7,
          title: "Editing & Deleting",
          description: "Modify existing data as needed",
          icon: Settings,
          substeps: [
            { 
              text: "Edit Properties:", 
              detail: "Visit https://qpsaudit.com/properties → Click 'Edit' icon next to any property → Modify: property name, location, contact details (manager name/phone/email), notes → Click 'Save Changes' → Cannot change assigned template (would affect assessments) → Click 'Delete' button with caution (only if property has no assessments)." 
            },
            { 
              text: "Edit Users:", 
              detail: "Visit https://qpsaudit.com/users → Click 'Edit' icon next to any user → Modify: name, email, phone, role, assigned property (for managers) → Click 'Update User' → Password cannot be changed here (user must use 'Forgot Password' on login page)." 
            },
            { 
              text: "Delete Users:", 
              detail: "On users page, click 'Delete' icon → Popup asks 'Are you sure?' → Confirm deletion → User account permanently removed → They can no longer login → Warning: Only delete inactive or duplicate users. Do not delete users with active assessments." 
            },
            { 
              text: "Edit Assessments:", 
              detail: "Visit https://qpsaudit.com/assessments → Click 'Edit' icon next to Draft or In Progress assessments → Can modify: assessment date, selected departments/touchpoints (scope), assigned auditor → Cannot edit Completed assessments (results are final) → Click 'Save' to update." 
            },
            { 
              text: "Delete Assessments:", 
              detail: "On assessments page, click 'Delete' icon → Confirmation popup appears → Confirm deletion → Assessment permanently removed along with all photos, audio, and results → Warning: Only delete cancelled or duplicate assessments. Cannot recover after deletion." 
            },
            { 
              text: "Edit Templates & Standards:", 
              detail: "Visit https://qpsaudit.com/admin/templates → Click any template → Click 'Edit' icon next to any standard → Modify department, touchpoint, name, or description → Click 'Save' → Changes apply to all properties using this template → Existing assessments are NOT affected (they keep original standards)." 
            },
            { 
              text: "Safety Confirmations:", 
              detail: "All delete actions require clicking 'Confirm' in a popup dialog → This prevents accidental deletions → Popup shows what will be deleted and warns about consequences → You can click 'Cancel' to abort → After confirming, deletion is immediate and permanent (no undo)." 
            }
          ]
        }
      ]
    },

    admin: {
      title: "Admin Guidelines", 
      description: "View all system data and manage assessments",
      color: "bg-blue-500",
      steps: [
        {
          number: 1,
          title: "Viewing Templates",
          description: "Review property type templates and standards (read-only access)",
          icon: Settings,
          substeps: [
            { 
              text: "Navigate to Templates:", 
              detail: "Click 'Templates' in sidebar or visit https://qpsaudit.com/admin/templates → You'll see all property type templates (e.g., Hotel, Resort, Villa) with their total standard counts." 
            },
            { 
              text: "View Template Details:", 
              detail: "Click any template card to open its details page (e.g., https://qpsaudit.com/admin/templates/1) → See complete list of departments, touchpoints, and standards → Standards are organized hierarchically: Department → Touchpoint → Individual Standards." 
            },
            { 
              text: "Search and Filter Standards:", 
              detail: "Use search bar at top to find standards by name (e.g., type 'bathroom' to find all bathroom-related standards) → Use Department dropdown filter to show only specific departments (e.g., 'Housekeeping') → Use Touchpoint dropdown to narrow further (e.g., 'Room Cleaning') → Combine search + filters for precise results → Click 'Clear Filters' to reset." 
            },
            { 
              text: "Review Organization:", 
              detail: "Each standard shows: Department name (e.g., 'Front Desk'), Touchpoint name (e.g., 'Check-in Process'), Standard code/ID, Standard name and description → This structure is used in all assessments for this template." 
            },
            { 
              text: "Export Template:", 
              detail: "Click 'Export to Excel' button → Downloads Excel file with all standards → Use for reference, documentation, or sharing with stakeholders → Excel includes columns: Department, Touchpoint, Standard Name, Description." 
            },
            { 
              text: "Read-Only Access:", 
              detail: "Important: As Admin, you can only VIEW templates → You cannot create new templates → You cannot add, edit, or delete standards → Only Super Admin has template edit permissions → Contact Super Admin if template changes are needed." 
            }
          ]
        },
        {
          number: 2,
          title: "Viewing Properties",
          description: "Access all property information in the system",
          icon: Building2,
          substeps: [
            { 
              text: "Navigate to Properties:", 
              detail: "Click 'Properties' in sidebar or visit https://qpsaudit.com/properties → See complete list of all registered properties → Each property card shows: property name, location, property type, manager name, total assessments count." 
            },
            { 
              text: "View Property Details:", 
              detail: "Click any property card to open its dashboard (e.g., https://qpsaudit.com/properties/12) → See property information: name, full address, contact details (manager name, phone, email), property type template applied, registration date." 
            },
            { 
              text: "Review Property Dashboard:", 
              detail: "Property dashboard shows: All assessments for this property (list with dates and status), Compliance rate over time (line chart showing trends), Missed standards report (standards that failed in recent assessments), Department performance (which departments score highest/lowest), Recent assessment results." 
            },
            { 
              text: "View Missed Standards:", 
              detail: "Scroll to 'Missed Standards' section → Shows standards marked 'Not Met' in recent assessments → Organized by assessment date, department, and touchpoint → Click on any missed standard to see photos and audio evidence → Use this to identify recurring quality issues." 
            },
            { 
              text: "Track Performance Trends:", 
              detail: "Charts show compliance percentage over time → Green trend line = improving, Red = declining → Compare different departments to identify strengths and weaknesses → Use data to plan training or operational improvements." 
            },
            { 
              text: "Read-Only Access:", 
              detail: "Important: As Admin, you can only VIEW properties → You cannot create new properties → You cannot edit property details or delete properties → Only Super Admin has property edit permissions → Contact Super Admin for property changes." 
            }
          ]
        },
        {
          number: 3,
          title: "Managing Users",
          description: "View and manage user accounts (full access)",
          icon: Users,
          substeps: [
            { 
              text: "Navigate to Users:", 
              detail: "Click 'Users' in sidebar or visit https://qpsaudit.com/users → See table of all users with: name, email, role, assigned property (for managers), action buttons." 
            },
            // { 
            //   text: "Create New User:", 
            //   detail: "Click 'Create New User' button (top right) → Opens form at https://qpsaudit.com/users/create-user → Fill: Full Name, Email (must be unique, used for login), Password (minimum 8 characters), Phone Number (with country code)." 
            // },
            // { 
            //   text: "Select Role - Admin:", 
            //   detail: "Choose 'Admin' for users who need to: View all data (read-only templates/properties), Create and manage assessments, Manage all users, Export reports. Admins see same interface as you do." 
            // },
            // { 
            //   text: "Select Role - Manager:", 
            //   detail: "Choose 'Manager' for property managers who need to: View ONLY their property dashboard, Monitor assessment results, Review missed standards, Track performance trends. After selecting Manager, you MUST assign which property they manage from dropdown." 
            // },
            // { 
            //   text: "Select Role - Auditor:", 
            //   detail: "Choose 'Auditor' for field inspectors who need to: Perform on-site assessments, Assess standards (Met/Not Met/N/A), Upload photos and audio, Submit completed assessments. Auditors are assigned to specific assessments when you create them." 
            // },
            // { 
            //   text: "Save and Notify User:", 
            //   detail: "Click 'Create User' → User account created immediately → User can login at https://qpsaudit.com/login with their email and password → They see role-appropriate interface." 
            // },
            // { 
            //   text: "Edit Existing Users:", 
            //   detail: "On users page (https://qpsaudit.com/users), click 'Edit' icon → Modify: name, email, phone, role, assigned property → Click 'Update User' to save changes → Changes take effect immediately on their next action." 
            // },
            // { 
            //   text: "Delete Users:", 
            //   detail: "Click 'Delete' icon next to any user → Confirmation popup appears → Warning: User account will be permanently removed → Confirm deletion → User can no longer login → Only delete inactive or duplicate accounts." 
            // }
          ]
        },
        {
          number: 4,
          title: "Creating Assessments",
          description: "Schedule quality assessments for any property",
          icon: ClipboardCheck,
          substeps: [
            { 
              text: "Navigate to Assessments:", 
              detail: "Click 'Assessments' in sidebar or visit https://qpsaudit.com/assessments → See all assessments (yours and others) → Table shows: property, auditor, date, status, actions." 
            },
            { 
              text: "Start Creating:", 
              detail: "Click 'Create Assessment' button (top right) → Opens form at https://qpsaudit.com/assessments/create-assessment" 
            },
            { 
              text: "Select Property:", 
              detail: "Choose property from dropdown → All registered properties appear → Selected property determines which template standards will be used → Property name and type display after selection." 
            },
            { 
              text: "Choose Date:", 
              detail: "Click date picker → Select assessment date (today or future) → Date helps schedule auditor workload → Past dates disabled to prevent backdating." 
            },
            { 
              text: "Define Scope - Select Departments:", 
              detail: "See checkboxes for all departments from property's template (e.g., Housekeeping, Front Desk, Restaurant, Pool) → Check departments you want assessed → Leave unchecked to skip departments → Selecting department includes all its touchpoints by default → Use to focus on specific areas." 
            },
            { 
              text: "Define Scope - Select Touchpoints:", 
              detail: "Expand selected departments to see touchpoints → Uncheck specific touchpoints to exclude (e.g., assess 'Housekeeping' but skip 'Laundry' touchpoint) → This gives granular control → Auditor only sees selected departments/touchpoints." 
            },
            { 
              text: "Assign Auditor:", 
              detail: "Select from dropdown which Auditor will perform assessment → Only users with 'Auditor' role appear → Assigned auditor sees this assessment in their list when they login." 
            },
            { 
              text: "Save Assessment:", 
              detail: "Click 'Create Assessment' → Assessment created with status 'Draft' → Appears in assessments list immediately → Assigned auditor can access at https://qpsaudit.com/assessments and click 'Perform' → You can edit details before auditor starts." 
            }
          ]
        },
        {
          number: 5,
          title: "Monitoring Assessments",
          description: "Track and review all assessment activities",
          icon: Eye,
          substeps: [
            { 
              text: "View All Assessments:", 
              detail: "Visit https://qpsaudit.com/assessments → See every assessment across all properties → Table columns: Property Name, Assigned Auditor, Assessment Date, Status (Draft/In Progress/Completed), Action buttons." 
            },
            { 
              text: "Use Filters:", 
              detail: "Search bar: type property name or auditor name to filter list → Status dropdown: filter by Draft/In Progress/Completed → Date range: show assessments within specific date period → Combine filters for precise results." 
            },
            { 
              text: "Understand Status:", 
              detail: "Draft (gray) = Created but not started, auditor hasn't assessed any standards yet → In Progress (blue) = Auditor is working, some standards assessed but not submitted → Completed (green) = Auditor submitted, all results final and visible to managers." 
            },
            { 
              text: "View Assessment Details:", 
              detail: "Click 'Perform' button → Opens at https://qpsaudit.com/assessments/perform/[id] → Left sidebar: departments and touchpoints → Click department → Click touchpoint → See all standards with Met (green) / Not Met (red) / N/A (gray) status." 
            },
            { 
              text: "Review Evidence:", 
              detail: "Each standard may have camera icon (photos) or microphone icon (audio) → Click camera to view full-size photos of issues or good examples → Click play on audio player to hear auditor's verbal explanation → Use evidence to understand findings clearly." 
            },
            { 
              text: "Export Reports:", 
              detail: "On assessment details page, click 'Export PDF' (top right) → System generates comprehensive PDF with: property details, assessment date and auditor, all standards with results, embedded photos, compliance statistics → PDF downloads automatically for documentation." 
            }
          ]
        },
        {
          number: 6,
          title: "Using the Dashboard",
          description: "Access analytics and system overview",
          icon: FileText,
          substeps: [
            { 
              text: "Access Dashboard:", 
              detail: "Click 'Dashboard' in sidebar or visit https://qpsaudit.com/dashboard → Main overview screen with system-wide statistics." 
            },
            { 
              text: "View Key Metrics:", 
              detail: "Top cards show: Total Properties (all registered), Total Assessments (all created), Total Users (all accounts), Completion Rate (% of completed assessments) → Each shows trend indicator (↑ up or ↓ down from last period)." 
            },
            { 
              text: "Monitor Status Breakdown:", 
              detail: "Assessment status chart: visual breakdown of Draft/In Progress/Completed → See how many assessments are pending vs done → Click segment to filter and view those specific assessments." 
            },
            { 
              text: "Track Trends:", 
              detail: "Compliance line chart shows rates over time (30/60/90 days) → Y-axis = compliance % (0-100%), X-axis = dates → Green trend = improving quality, Red = declining → Hover over points for exact values." 
            },
            { 
              text: "Review Activity Feed:", 
              detail: "Recent activity list shows: New assessments created, Assessments completed, New properties added, New users registered → Each entry has timestamp and actor name → Click entry to view details." 
            },
            { 
              text: "Identify At-Risk Properties:", 
              detail: "'Properties Needing Attention' highlights: Low compliance (below 70%), Increasing missed standards, Overdue assessments → Sorted by priority (worst first) → Click property name to investigate and take action." 
            }
          ]
        },
        {
          number: 7,
          title: "Editing & Deleting Assessments",
          description: "Manage assessment lifecycle",
          icon: Settings,
          substeps: [
            { 
              text: "Edit Draft or In Progress Assessments:", 
              detail: "Visit https://qpsaudit.com/assessments → Click 'Edit' icon next to any Draft or In Progress assessment → Can modify: assessment date, selected departments/touchpoints (scope), assigned auditor → Cannot edit Completed assessments (results are final) → Click 'Save Changes' to update." 
            },
            { 
              text: "Reassign Auditors:", 
              detail: "In edit mode, use Auditor dropdown to change who is assigned → Useful if original auditor is unavailable → New auditor immediately sees assignment in their list → Previous auditor loses access if assessment not started." 
            },
            { 
              text: "Modify Scope:", 
              detail: "In edit mode, check/uncheck departments and touchpoints → Add more areas to assess or reduce scope → Changes visible to auditor immediately → If assessment already In Progress, new standards appear as unassessed." 
            },
            { 
              text: "Delete Assessments:", 
              detail: "On assessments page, click 'Delete' icon (trash) → Confirmation popup: 'Are you sure? This will permanently delete assessment, photos, audio, and all results.' → Confirm deletion → Assessment removed from system → Cannot be recovered." 
            },
            { 
              text: "When to Delete:", 
              detail: "Delete when: Assessment was created by mistake (duplicate), Assessment cancelled (property no longer needs it), Wrong property or date selected. Do NOT delete completed assessments with important historical data." 
            }
          ]
        }
      ]
    },

    manager: {
      title: "Manager Guidelines",
      description: "Monitor and track your property's quality performance",
      color: "bg-green-500",
      steps: [
        {
          number: 1,
          title: "Accessing Your Property Dashboard",
          description: "View your property's performance and assessment results",
          icon: Home,
          substeps: [
            { 
              text: "Login to System:", 
              detail: "Visit https://qpsaudit.com/login → Enter your email and password (provided by Admin or Super Admin) → Click 'Login' button." 
            },
            { 
              text: "Automatic Redirect:", 
              detail: "After login, you're automatically redirected to your property's dashboard (e.g., https://qpsaudit.com/properties/12) → This is YOUR main workspace → You only see data for your assigned property (cannot view other properties)." 
            },
            { 
              text: "Dashboard Overview:", 
              detail: "Top section shows property details: Property name, Location, Property type (e.g., '5-Star Hotel'), Manager name (you), Contact information." 
            },
            { 
              text: "View Key Metrics:", 
              detail: "Metric cards display: Total Assessments (all assessments performed), Compliance Rate (average % of standards met), Recent Assessment Date (when last assessed), Missed Standards Count (total standards marked 'Not Met' recently)." 
            },
            { 
              text: "Performance Chart:", 
              detail: "Line chart shows compliance trends over time (last 30/60/90 days) → Y-axis = compliance % (0-100%), X-axis = dates → Green trend = improving, Red = declining → Hover over data points for exact values." 
            }
          ]
        },
        {
          number: 2,
          title: "Viewing Assessment Results",
          description: "Review completed assessments for your property",
          icon: ClipboardCheck,
          substeps: [
            { 
              text: "Find Assessments Section:", 
              detail: "On your property dashboard, scroll to 'Assessments' section → Shows table of all assessments for your property: Assessment date, Assigned auditor name, Status (Draft/In Progress/Completed), Action buttons." 
            },
            { 
              text: "Filter Assessments:", 
              detail: "Use date filter to show specific time period (e.g., last 30 days, last 6 months) → Use status filter to show only Completed assessments (most relevant) → Search by auditor name if needed." 
            },
            { 
              text: "View Assessment Details:", 
              detail: "Click 'View' or 'Perform' button next to any assessment → Opens assessment interface at https://qpsaudit.com/assessments/perform/[id] → Shows: Assessment date, Assigned auditor, Which departments and touchpoints were assessed (scope)." 
            },
            { 
              text: "Understand Assessment Scope:", 
              detail: "Left sidebar shows only the departments and touchpoints that were included in this assessment → Not all departments may be assessed (depends on what Admin selected when creating assessment) → Some touchpoints may be excluded even if department is included." 
            },
            { 
              text: "Read-Only Access:", 
              detail: "Important: As Manager, you can only VIEW assessments → You cannot assess standards (change Met/Not Met/N/A) → You cannot upload photos or audio → You cannot edit or delete assessments → Your access is read-only for monitoring purposes." 
            }
          ]
        },
        {
          number: 3,
          title: "Understanding Assessment Details",
          description: "Navigate through assessment results",
          icon: Eye,
          substeps: [
            { 
              text: "Navigate Department Sidebar:", 
              detail: "When viewing assessment (https://qpsaudit.com/assessments/perform/[id]), left sidebar lists departments → Example: Housekeeping, Front Desk, Restaurant, Pool & Beach → Click any department name to expand and see its touchpoints." 
            },
            { 
              text: "Select Touchpoint:", 
              detail: "After expanding department, touchpoints appear indented below (e.g., under 'Housekeeping': Room Cleaning, Bathroom Cleaning, Laundry) → Click touchpoint name → Main content area updates to show all standards for that touchpoint." 
            },
            { 
              text: "View Standard Results:", 
              detail: "Each standard displays: Standard code/ID, Standard name (e.g., 'Bed linens are clean and wrinkle-free'), Assessment result with color-coded badge. Results are: Met (green badge) = Standard achieved, compliant, Not Met (red badge) = Standard not achieved, issue found, N/A (gray badge) = Not applicable in this situation." 
            },
            { 
              text: "Interpret Color Coding:", 
              detail: "Green = Good! Standard was met during assessment. Property is compliant. Red = Issue! Standard was not met. Requires attention or corrective action. Gray = N/A (Not Applicable). Standard doesn't apply in current context (e.g., 'Pool cleanliness' when property has no pool)." 
            },
            { 
              text: "View Photos and Audio:", 
              detail: "Standards with photos show camera icon or thumbnail image → Standards with audio show microphone icon and audio player → Click to view/listen for detailed evidence and context → Especially useful for understanding 'Not Met' issues." 
            },
            { 
              text: "Navigation Tips:", 
              detail: "Work systematically: go through each department one by one → Focus on 'Not Met' (red) standards first to identify issues → Use browser back button or sidebar to switch between departments → All results are read-only, you cannot modify them." 
            }
          ]
        },
        {
          number: 4,
          title: "Reviewing Missed Standards",
          description: "Identify areas that need improvement",
          icon: ListChecks,
          substeps: [
            { 
              text: "Access Missed Standards:", 
              detail: "Return to your property dashboard (https://qpsaudit.com/properties/[your-property-id]) → Scroll to 'Missed Standards' section (usually below metrics cards) → This section aggregates ALL standards marked 'Not Met' across recent assessments." 
            },
            { 
              text: "Understand Organization:", 
              detail: "Missed standards are grouped by: Assessment Date (most recent first), Department (e.g., Housekeeping), Touchpoint (e.g., Room Cleaning), Individual Standard (e.g., 'Bed linens clean'). This hierarchical structure helps identify patterns." 
            },
            { 
              text: "View Evidence:", 
              detail: "Each missed standard shows: Standard name and description, Assessment date when it was marked 'Not Met', Photos uploaded by auditor (click to enlarge), Audio notes with auditor's explanation → Use this evidence to understand exact issue and severity." 
            },
            { 
              text: "Identify Recurring Issues:", 
              detail: "Look for standards that appear multiple times across different assessment dates → These are recurring problems needing systematic fixes (training, procedures, equipment) → One-time issues may be isolated incidents." 
            },
            { 
              text: "Plan Corrective Actions:", 
              detail: "Use missed standards report to: Create training plans for staff (e.g., housekeeping team retraining on bed-making), Update Standard Operating Procedures (SOPs), Replace faulty equipment or supplies, Schedule maintenance or repairs, Set priorities based on frequency and severity." 
            },
            { 
              text: "Track Improvements:", 
              detail: "After implementing fixes, monitor next assessment results → Previously missed standards should show 'Met' (green) in future assessments → Compare assessment dates to measure improvement over time → Use data to demonstrate quality improvements to management." 
            }
          ]
        },
        {
          number: 5,
          title: "Viewing Evidence Photos & Audio",
          description: "Review documentation provided by Auditors",
          icon: Upload,
          substeps: [
            { 
              text: "Find Attachments:", 
              detail: "When viewing assessment details (https://qpsaudit.com/assessments/perform/[id]), each standard may have: Camera icon or photo thumbnail (indicates photos), Microphone icon or audio player (indicates audio recording). Not all standards have attachments - auditors add them when needed." 
            },
            { 
              text: "View Photos:", 
              detail: "Click photo thumbnail or camera icon → Opens full-size image viewer → Photos show: Evidence of issues for 'Not Met' standards (e.g., stained linen, dirty surface), Evidence of excellence for 'Met' standards (e.g., perfectly made bed) → Use arrow keys or swipe gestures to navigate between multiple photos → Click 'X' or press Escape to close viewer." 
            },
            { 
              text: "Photo Analysis:", 
              detail: "Photos provide visual proof of assessment findings → For 'Not Met' standards: photos show exactly what's wrong (helps understand issue and plan fix) → For 'Met' standards: photos show best practices (use as training examples for staff) → Clear angle and lighting help assess severity." 
            },
            { 
              text: "Listen to Audio Notes:", 
              detail: "Standards with audio show microphone icon and audio player control → Click play button (▶) to start playback → Auditor's voice explains: Why standard wasn't met (context), Severity of the issue (minor vs critical), Suggestions for improvement, Additional observations not captured in photos → Use pause, rewind controls as needed." 
            },
            { 
              text: "Audio Benefits:", 
              detail: "Audio provides context photos can't capture → Example: Photo shows dirty floor, audio explains 'Floor was just mopped but with dirty water' → Helps understand root cause → Typical length: 20-60 seconds per standard → Especially valuable for complex or nuanced findings." 
            },
            { 
              text: "Use Evidence for Action:", 
              detail: "Combine photos + audio + written notes for complete understanding → Share evidence with relevant team members (e.g., show housekeeping supervisor photos of issues) → Use as training materials: 'This is what NOT to do' or 'This is excellent' → Document evidence in your corrective action plans." 
            }
          ]
        },
        {
          number: 6,
          title: "Monitoring Property Performance",
          description: "Track quality trends over time",
          icon: FileText,
          substeps: [
            { 
              text: "Dashboard Performance Section:", 
              detail: "On your property dashboard (https://qpsaudit.com/properties/[your-id]), performance charts show trends across all assessments → Main chart: Compliance Rate Over Time (line chart) → X-axis = assessment dates, Y-axis = compliance percentage (0-100%)." 
            },
            { 
              text: "Interpret Trend Lines:", 
              detail: "Green upward trend = Quality improving over time (good progress!) → Red downward trend = Quality declining (needs urgent attention) → Flat horizontal = Stable quality (no improvement or decline) → Hover over data points to see exact compliance rate on specific date." 
            },
            { 
              text: "View Compliance by Department:", 
              detail: "Department breakdown chart shows: Which departments score highest (consistently meet standards) → Which departments score lowest (frequent issues) → Example: 'Housekeeping 92%, Front Desk 88%, Restaurant 75%' → Use to prioritize where to focus improvement efforts." 
            },
            { 
              text: "Analyze Touchpoint Performance:", 
              detail: "Drill down to see touchpoint-level data → Example under 'Housekeeping': 'Room Cleaning 95%, Bathroom Cleaning 90%, Public Areas 85%' → Identify specific touchpoints that consistently meet or miss standards → Helps target training to specific tasks." 
            },
            { 
              text: "Compare Assessment Periods:", 
              detail: "Use date filters to compare: Last 30 days vs previous 30 days, Current quarter vs previous quarter, Year-over-year comparison → Shows whether improvements are working → Quantify impact of training, equipment upgrades, or process changes." 
            },
            { 
              text: "Use Data for Quality Initiatives:", 
              detail: "Present performance data in team meetings to: Celebrate improvements and recognize good performers, Identify training needs based on low-scoring areas, Set realistic quality improvement goals, Demonstrate ROI of quality investments to upper management, Share insights with staff to drive accountability and engagement." 
            }
          ]
        },
        {
          number: 7,
          title: "Property Information",
          description: "View your property details and contact information",
          icon: Building2,
          substeps: [
            { 
              text: "Access Property Details:", 
              detail: "On your dashboard (https://qpsaudit.com/properties/[your-id]), top section shows complete property information card → All details about your property are displayed here." 
            },
            { 
              text: "Basic Information:", 
              detail: "View: Property Name (e.g., 'Grand Hyatt Dubai'), Full Address (street, city, country), Property Type (e.g., '5-Star Hotel', 'Boutique Resort'), Registration Date (when property was added to system)." 
            },
            { 
              text: "Contact Information:", 
              detail: "See: Manager Name (your name), Manager Phone (your contact number), Manager Email (your login email), Additional Notes (any special information about the property). This info is visible to Admins and Auditors when they work with your property." 
            },
            { 
              text: "Applied Template:", 
              detail: "Shows which Property Type Template is applied to your property → This template determines which standards are assessed → Example: 'Hotel Standards Template - 450 total standards' → Click template name to see all standards (read-only view)." 
            },
            { 
              text: "Request Information Updates:", 
              detail: "As Manager, you cannot edit property information yourself → If any information is incorrect or outdated (phone number changed, manager name, etc.) → Contact your Admin or Super Admin → Provide exact corrections needed → They will update information in the system." 
            },
            { 
              text: "Why Information Matters:", 
              detail: "Accurate contact info ensures: Auditors can reach you during assessments, Admins can contact you for scheduling, Reports have correct property details, System-generated emails go to right person. Keep your contact information current." 
            }
          ]
        }
      ]
    },

    auditor: {
      title: "Auditor Guidelines",
      description: "Perform quality assessments and document findings",
      color: "bg-purple-500",
      steps: [
        {
          number: 1,
          title: "Viewing Your Assigned Assessments",
          description: "See assessments that have been assigned to you",
          icon: ClipboardCheck,
          substeps: [
            { 
              text: "Login to System:", 
              detail: "Visit https://qpsaudit.com/login → Enter your email and password (provided by Admin) → Click 'Login' button." 
            },
            { 
              text: "Automatic Redirect to Assessments:", 
              detail: "After login, you're automatically taken to https://qpsaudit.com/assessments → This is YOUR workspace → You only see assessments assigned to you (not assessments assigned to other auditors)." 
            },
            { 
              text: "Assessments List:", 
              detail: "Table shows your assignments with columns: Property Name (where you'll perform assessment), Assessment Date (when assessment is scheduled), Status (Draft/In Progress/Completed), Scope (which departments/touchpoints included), Action Button ('Perform' to start/continue)." 
            },
            { 
              text: "Understand Status:", 
              detail: "Draft (gray badge) = You haven't started yet, no standards assessed → In Progress (blue badge) = You've started, some standards assessed but not submitted → Completed (green badge) = You submitted the assessment, results are final and visible to managers." 
            },
            { 
              text: "Review Scope Before Starting:", 
              detail: "Hover over or click 'View Scope' to see which departments and touchpoints are included → Example: 'Housekeeping (Room Cleaning, Bathrooms), Front Desk (Check-in, Check-out)' → This helps you plan your inspection route and time needed." 
            },
            { 
              text: "Start or Continue:", 
              detail: "Click 'Perform' button next to any assessment → Draft assessments start from beginning → In Progress assessments resume where you left off (all previous answers saved) → Completed assessments are view-only (cannot edit after submission)." 
            }
          ]
        },
        {
          number: 2,
          title: "Starting an Assessment",
          description: "Begin the inspection process",
          icon: ArrowRight,
          substeps: [
            { 
              text: "Click 'Perform' Button:", 
              detail: "On assessments list (https://qpsaudit.com/assessments), click 'Perform' next to your assigned assessment → Opens assessment interface at https://qpsaudit.com/assessments/perform/[id] → This is where you'll spend most of your time during the inspection." 
            },
            { 
              text: "Review Assessment Header:", 
              detail: "Top of page shows: Property Name (where you're assessing), Property Address (in case you need directions), Assessment Date (today's date), Your name (assigned auditor), Progress indicator (e.g., '45 of 120 standards assessed'). Keep header visible for reference." 
            },
            { 
              text: "Understand Left Sidebar:", 
              detail: "Sidebar lists all departments included in this assessment → Example: Housekeeping, Front Desk, Restaurant, Pool & Beach → Each department shows: Department name, Number of touchpoints, Number of standards to assess → Click department to expand and see touchpoints." 
            },
            { 
              text: "Expand Department:", 
              detail: "Click on any department name (e.g., 'Housekeeping') → Department expands to reveal its touchpoints → Example touchpoints: Room Cleaning (25 standards), Bathroom Cleaning (18 standards), Public Areas (12 standards) → Number in parentheses = total standards in that touchpoint." 
            },
            { 
              text: "Select Starting Point:", 
              detail: "You can start with ANY department or touchpoint (no required order) → Recommended: Start with first department, work through all touchpoints, then move to next department → OR: Follow your physical inspection route (e.g., start at entrance, work through building) → System saves progress regardless of order." 
            },
            { 
              text: "Click Touchpoint to Begin:", 
              detail: "Click any touchpoint name (e.g., 'Room Cleaning') → Main content area updates to show all standards for that touchpoint → Standards list appears with assessment options → Now you're ready to start assessing!" 
            }
          ]
        },
        {
          number: 3,
          title: "Assessing Standards",
          description: "Evaluate each quality standard systematically",
          icon: ListChecks,
          substeps: [
            { 
              text: "View Standards List:", 
              detail: "After clicking a touchpoint, main area shows all its standards → Each standard displays: Standard Code/ID (e.g., 'HS-001'), Standard Name (e.g., 'Bed linens are clean, fresh, and wrinkle-free'), Standard Description (detailed criteria for compliance), Three assessment buttons: Met, Not Met, N/A." 
            },
            { 
              text: "Select 'Met' (Green):", 
              detail: "Click 'Met' button if: Standard is fully achieved, compliant with all criteria, No issues or deficiencies found, Quality meets expectations → Example: Bed linens ARE clean, fresh, and wrinkle-free → Button turns green, system saves automatically → Consider taking a photo of excellent examples for training purposes." 
            },
            { 
              text: "Select 'Not Met' (Red):", 
              detail: "Click 'Not Met' button if: Standard is NOT achieved, Any part of criteria is not compliant, Issues or deficiencies found, Quality below expectations → Example: Bed linens have stains OR wrinkles OR smell musty → Button turns red, system saves automatically → IMPORTANT: Always take photos and/or audio notes for 'Not Met' standards to document the issue." 
            },
            { 
              text: "Select 'N/A' (Gray):", 
              detail: "Click 'N/A' button if: Standard doesn't apply in this specific situation, Facility/equipment/service not available at property, Context makes standard irrelevant → Example: 'Pool water clarity' when property has no pool, 'Breakfast buffet presentation' when no breakfast service offered → Button turns gray → Use N/A sparingly - verify with manager if unsure." 
            },
            { 
              text: "Automatic Saving:", 
              detail: "Every selection saves immediately to database → No need to click 'Save' button → If internet disconnects, system retries when connection restored → Green checkmark appears briefly confirming save → You can safely close browser and resume later - all progress preserved." 
            },
            { 
              text: "Work Through Standards:", 
              detail: "Assess each standard one by one, top to bottom → Don't skip standards (all must have Met/Not Met/N/A selected) → Progress bar at top updates as you go (e.g., '48 of 120 standards') → Scroll down to see more standards in current touchpoint → When finished with touchpoint, click next touchpoint in sidebar." 
            }
          ]
        },
        {
          number: 4,
          title: "Taking Photos",
          description: "Document your findings with visual evidence",
          icon: Upload,
          substeps: [
            { 
              text: "When to Take Photos:", 
              detail: "REQUIRED for 'Not Met' standards (red) - photos provide proof of issue → RECOMMENDED for 'Met' standards (green) - photos show best practices/examples → NOT needed for 'N/A' standards (gray) - no point documenting something that doesn't apply → Photos are critical evidence managers rely on." 
            },
            { 
              text: "Click Camera Icon:", 
              detail: "Next to each standard, click the camera icon → System prompts for photo access (allow camera permissions if first time) → Mobile: Opens device camera app directly → Desktop: Opens file picker to select existing photos OR activate webcam → Take multiple photos from different angles if needed." 
            },
            { 
              text: "Take Quality Photos:", 
              detail: "Photo best practices: Good lighting (avoid dark or overexposed shots), Clear focus (no blurry images), Appropriate angle (show issue clearly), Include context (not too zoomed in), Stable shot (avoid camera shake) → For 'Not Met': Focus on the problem area (e.g., close-up of stain on linen) → For 'Met': Show overall excellence (e.g., perfectly arranged room)." 
            },
            { 
              text: "Upload Multiple Photos:", 
              detail: "You can upload 2-5 photos per standard → Example for 'Bathroom cleanliness': Photo 1 = overall view, Photo 2 = close-up of dirty grout, Photo 3 = stained fixtures → Multiple angles provide complete picture → Each photo uploads automatically after capture → Green checkmark confirms successful upload." 
            },
            { 
              text: "Review Uploaded Photos:", 
              detail: "Uploaded photos appear as thumbnails below standard → Click thumbnail to view full-size → If photo is unclear or wrong, click 'X' to delete and retake → Photos are immediately visible to Admins and Managers viewing assessment progress → Ensure photos are clear before moving to next standard." 
            },
            { 
              text: "Photography Tips:", 
              detail: "Hold phone/camera steady with both hands → Tap screen to focus before capturing → Use natural light when possible (avoid flash if too harsh) → For small issues, get closer for detail → For overall compliance, step back for context → Clean camera lens if photos look foggy → Take extra photos if unsure - can delete later." 
            }
          ]
        },
        {
          number: 5,
          title: "Recording Audio Notes",
          description: "Add verbal explanations and context",
          icon: Upload,
          substeps: [
            { 
              text: "When to Record Audio:", 
              detail: "RECOMMENDED for 'Not Met' standards - explain why it didn't meet criteria → USEFUL for complex situations - provide context photos can't capture → HELPFUL for suggestions - record recommendations for improvement → Audio complements photos and provides complete documentation." 
            },
            { 
              text: "Click Microphone Icon:", 
              detail: "Next to each standard, click the microphone icon → System prompts for microphone access (allow if first time) → Recording interface appears with: Red record button, Timer showing recording length (0:00), Stop button (appears after starting), Cancel option." 
            },
            { 
              text: "Record Your Note:", 
              detail: "Click red record button to start → Speak clearly into device microphone: State the standard name/number, Explain what you observed, Why it didn't meet criteria (for 'Not Met'), Severity of issue (minor, moderate, critical), Suggestions for improvement → Keep recording 30-60 seconds (concise but complete) → Click stop button when finished." 
            },
            { 
              text: "Example Audio Scripts:", 
              detail: "For 'Not Met': 'Standard HS-015 Bed Linens. The bed sheets have visible stains on the left side and wrinkles throughout. The pillowcases also appear unwashed. This is a hygiene issue requiring immediate attention. Recommend replacing linens and retraining housekeeping staff on bed-making procedures.' → For 'Met': 'Standard FD-003 Check-in Process. Front desk staff was professional, efficient, and welcoming. Guest was greeted within 30 seconds, check-in completed in under 5 minutes, all documents properly explained. Excellent performance.'" 
            },
            { 
              text: "Review and Upload:", 
              detail: "After stopping, system shows: Recording length (e.g., '0:45'), Playback button (listen to review), Re-record button (if not satisfied), Upload button (to save) → Click playback to verify audio is clear → If not clear, click re-record and try again → Click upload to save → Green checkmark confirms successful upload." 
            },
            { 
              text: "Audio Recording Tips:", 
              detail: "Find quiet location (minimize background noise) → Speak at normal volume (not too soft or loud) → Hold device close to mouth (6-12 inches) → Speak clearly and slowly (easier to understand) → Be professional (managers will listen) → Stay objective (describe what you see, not opinions) → Be specific (exact locations, clear descriptions)." 
            }
          ]
        },
        {
          number: 6,
          title: "Navigating Between Areas",
          description: "Work through the assessment systematically",
          icon: ArrowRight,
          substeps: [
            { 
              text: "Use Left Sidebar:", 
              detail: "Sidebar remains visible throughout assessment → Click any department name to expand/collapse its touchpoints → Click any touchpoint name to load its standards → Current touchpoint is highlighted in blue → Completed touchpoints show green checkmark → Incomplete touchpoints show gray dot." 
            },
            { 
              text: "Systematic Approach:", 
              detail: "Recommended workflow: Start with first department (e.g., 'Housekeeping') → Complete all its touchpoints one by one (Room Cleaning → Bathrooms → Public Areas) → Move to next department (e.g., 'Front Desk') → Repeat until all departments done → This ensures nothing is missed and progress is clear." 
            },
            { 
              text: "Physical Inspection Route:", 
              detail: "Alternative: Follow your physical path through property → Example: Start at main entrance (Front Desk standards) → Walk to guest rooms (Housekeeping) → Visit pool area (Pool & Beach) → End at restaurant (F&B) → Click matching touchpoints in sidebar as you inspect → Reduces walking back and forth." 
            },
            { 
              text: "Progress Tracking:", 
              detail: "Top progress bar shows overall completion: 'Assessed 78 of 120 standards (65%)' → Each department shows its completion: 'Housekeeping: 25/35 standards' → Green checkmarks appear on completed touchpoints → Sidebar scrolls to keep current touchpoint visible → Use progress indicators to plan time and breaks." 
            },
            { 
              text: "Auto-Save Feature:", 
              detail: "Every action saves automatically - no 'Save' button needed → You can: Pause assessment anytime (close browser, turn off device), Resume later exactly where you left off, Switch between touchpoints freely, Internet disconnection won't lose data (saves when reconnects) → Take breaks as needed - progress preserved." 
            },
            { 
              text: "Work at Your Own Pace:", 
              detail: "No time limit on assessments (but complete within assigned date if possible) → Typical property: 2-4 hours depending on size and scope → Large properties: Can split across multiple sessions → Take time to be thorough and accurate → Quality of assessment more important than speed." 
            }
          ]
        },
        {
          number: 7,
          title: "Completing the Assessment",
          description: "Submit your completed inspection",
          icon: CheckCircle2,
          substeps: [
            { 
              text: "Pre-Submission Checklist:", 
              detail: "Before submitting, verify: ☑ All departments visited, ☑ All touchpoints assessed, ☑ Every standard has Met/Not Met/N/A selected (no blanks), ☑ All 'Not Met' standards have photos, ☑ Audio notes recorded for complex issues, ☑ Photos are clear and show issues properly → Use progress bar to confirm 100% completion: '120 of 120 standards assessed'." 
            },
            { 
              text: "Review Your Work:", 
              detail: "Scroll through sidebar and click each touchpoint → Quickly review standards to catch any mistakes or missing evidence → Check for: Standards accidentally left blank, Photos that are too dark or blurry (retake if needed), Missing audio for major issues → Better to catch errors now than after submission." 
            },
            { 
              text: "Locate Submit Button:", 
              detail: "Submit button appears at TOP and BOTTOM of page when assessment is 100% complete → Button is large, green, and says 'Submit Assessment' → If button is disabled (gray): System will show message like 'Complete all standards before submitting' or '15 standards remaining' → Complete missing items until button turns green." 
            },
            { 
              text: "Click Submit:", 
              detail: "Click 'Submit Assessment' button → Confirmation popup appears: 'Are you sure? Once submitted, you cannot edit assessment results. All data will be final and visible to Managers and Admins.' → Double-check you're ready → Click 'Confirm Submission' to finalize → Click 'Cancel' if you need more time." 
            },
            { 
              text: "After Submission:", 
              detail: "System processes submission (2-3 seconds) → Success message: 'Assessment submitted successfully!' → Status changes from 'In Progress' (blue) to 'Completed' (green) → You're redirected to assessments list at https://qpsaudit.com/assessments → Submitted assessment now shows 'Completed' badge → Results immediately visible to property Manager and all Admins/Super Admins." 
            },
            { 
              text: "Post-Submission:", 
              detail: "You can still view completed assessment (click 'View' button) → All standards, photos, and audio are visible in READ-ONLY mode → You CANNOT edit or change any answers after submission (results are final) → If you discover a mistake after submission: Contact Admin or Super Admin to delete assessment so you can re-do it → Manager receives notification of completed assessment and can begin reviewing." 
            }
          ]
        }
      ]
    }
  };

  const currentGuideline = guidelines[role];

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <div className={cn("flex items-center gap-3 mb-4", isRTL && "flex-row-reverse")}>
          <BookOpen className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">{t('guidelines.pageTitle')}</h1>
        </div>
        <Card>
          <CardHeader>
            <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
              <Badge className={cn(currentGuideline.color, "text-white px-3 py-1")}>
                {currentGuideline.title}
              </Badge>
            </div>
            <CardDescription className={isRTL ? "text-right" : ""}>
              {currentGuideline.description}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Guidelines Steps */}
      <div className="space-y-6">
        {currentGuideline.steps.map((step, index) => (
          <div key={index}>
            <Step {...step} />
            {index < currentGuideline.steps.length - 1 && (
              <Separator className="my-6" />
            )}
          </div>
        ))}
      </div>

      {/* Clipboard Permission Requirement - For All Users */}
      <Separator className="my-8" />
      
      <Card className="border-2 border-orange-500/50 bg-orange-50/50 dark:bg-orange-950/20">
        <CardHeader>
          <div className={cn("flex items-center gap-3 mb-4", isRTL && "flex-row-reverse")}>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-orange-500 text-white">
              🔒
            </div>
            <h2 className="text-2xl font-bold text-orange-900 dark:text-orange-100">
              {t('guidelines.clipboardPermission.title')}
            </h2>
          </div>
          <CardDescription className={cn("text-base", isRTL && "text-right")}>
            {t('guidelines.clipboardPermission.description')}
          </CardDescription>
        </CardHeader>
        <div className="px-6 pb-6">
          <div className="space-y-4">
            {/* Why Required */}
            <div className="p-4 bg-white dark:bg-gray-900 rounded-lg">
              <h3 className={cn("font-semibold text-lg mb-2 text-orange-800 dark:text-orange-200", isRTL && "text-right")}>
                {t('guidelines.clipboardPermission.whyRequired')}
              </h3>
              <p className={cn("text-muted-foreground", isRTL && "text-right")}>
                {t('guidelines.clipboardPermission.whyRequiredDetail')}
              </p>
            </div>

            {/* How to Enable */}
            <div className="p-4 bg-white dark:bg-gray-900 rounded-lg">
              <h3 className={cn("font-semibold text-lg mb-3 text-orange-800 dark:text-orange-200", isRTL && "text-right")}>
                {t('guidelines.clipboardPermission.howToEnable')}
              </h3>
              <ol className={cn("space-y-2", isRTL ? "list-none" : "list-decimal", "list-inside")}>
                <li className={cn("text-muted-foreground", isRTL && "text-right")}>
                  <span className={cn(isRTL && "mr-2")}>{t('guidelines.clipboardPermission.step1')}</span>
                </li>
                <li className={cn("text-muted-foreground", isRTL && "text-right")}>
                  <span className={cn(isRTL && "mr-2")}>{t('guidelines.clipboardPermission.step2')}</span>
                </li>
                <li className={cn("text-muted-foreground", isRTL && "text-right")}>
                  <span className={cn(isRTL && "mr-2")}>{t('guidelines.clipboardPermission.step3')}</span>
                </li>
                <li className={cn("text-muted-foreground", isRTL && "text-right")}>
                  <span className={cn(isRTL && "mr-2")}>{t('guidelines.clipboardPermission.step4')}</span>
                </li>
              </ol>
            </div>

            {/* What Happens if Blocked */}
            <div className="p-4 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-900">
              <h3 className={cn("font-semibold text-lg mb-2 text-red-800 dark:text-red-200", isRTL && "text-right")}>
                {t('guidelines.clipboardPermission.ifBlocked')}
              </h3>
              <p className={cn("text-red-700 dark:text-red-300", isRTL && "text-right")}>
                {t('guidelines.clipboardPermission.ifBlockedDetail')}
              </p>
            </div>

            {/* Note */}
            <div className={cn("flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg", isRTL && "flex-row-reverse")}>
              <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className={cn("text-sm text-blue-800 dark:text-blue-200", isRTL && "text-right")}>
                {t('guidelines.clipboardPermission.note')}
              </p>
            </div>
          </div>
        </div>
      </Card>
     
    </div>
  );
}
